// Talmia 2.0 — Profile & preferences.
// Lightweight page users land on from the header avatar menu.
// Shows current identity + role + placeholders for edit flows that will land
// after we wire up the /api/auth/me + /api/auth/profile endpoints.
import Layout from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { UserRound, Mail, Shield, LogOut, Sparkles, KeyRound, Bell, Lock } from "lucide-react";
import AvatarUpload from "../../components/squad/AvatarUpload";
import { T2Panel, T2Label, T2Chip } from "../quotes/_t2";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <Layout>
      <div data-testid="profile-page">
        <div style={{ marginBottom: 18 }}>
          <div className="t2-page-eyebrow">
            <UserRound className="w-3 h-3" /> Your account
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px", letterSpacing: "-.01em" }}>
            Profile & preferences
          </h1>
          <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
            Signed in as <span style={{ color: "var(--t2-white)" }}>{user.email}</span>
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 320px) 1fr", gap: 16 }}>
          {/* Identity card */}
          <T2Panel testid="profile-identity" title={null}>
            <div style={{ padding: 8 }}>
              <div style={{ display: "flex", justifyContent: "center", margin: "0 auto 12px" }}>
                <AvatarUpload size={72} />
              </div>
              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "var(--t2-white)" }}>{user.name}</div>
                <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-orange)", marginTop: 3 }}>
                  {user.email}
                </div>
                {user.title && <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey3)", marginTop: 4 }}>{user.title}</div>}
                <div style={{ marginTop: 8, display: "flex", justifyContent: "center", gap: 4 }}>
                  {user.role === "admin"
                    ? <T2Chip tone="orange">Admin</T2Chip>
                    : <T2Chip>User</T2Chip>}
                </div>
              </div>
              <button
                onClick={async () => { await logout(); navigate("/login"); }}
                className="t2-btn-ghost"
                style={{ width: "100%", justifyContent: "center", color: "var(--t2-red)", borderColor: "rgba(239,68,68,.3)" }}
                data-testid="profile-logout"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </T2Panel>

          {/* Settings panels */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <T2Panel icon={Shield} title="Account security" testid="profile-security">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <T2Label>Email</T2Label>
                  <div style={{ fontSize: 13, color: "var(--t2-white)" }}>{user.email}</div>
                </div>
                <div>
                  <T2Label>Role</T2Label>
                  <div style={{ fontSize: 13, color: "var(--t2-white)", textTransform: "capitalize" }}>{user.role}</div>
                </div>
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="t2-btn-ghost" disabled data-testid="btn-change-password"
                  style={{ opacity: .55, cursor: "not-allowed" }}>
                  <Lock className="w-3.5 h-3.5" /> Change password
                </button>
                <button className="t2-btn-ghost" disabled data-testid="btn-2fa"
                  style={{ opacity: .55, cursor: "not-allowed" }}>
                  <KeyRound className="w-3.5 h-3.5" /> Enable 2FA
                </button>
              </div>
              <div className="t2-mono" style={{ marginTop: 8, fontSize: 10, color: "var(--t2-grey4)" }}>
                In-app password change lands in the next auth pass. For now use the forgot-password flow from the login page.
              </div>
            </T2Panel>

            <T2Panel icon={Bell} title="Preferences" testid="profile-preferences">
              <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey3)", lineHeight: 1.6 }}>
                Preferences (email digest, default landing page, quote defaults) will live here soon. Ping the team when you have a feature in mind.
              </div>
            </T2Panel>

            <T2Panel icon={Sparkles} title="Universal Key balance" testid="profile-emergent">
              <div style={{ fontSize: 12.5, color: "var(--t2-grey1)", lineHeight: 1.5 }}>
                Talmia uses the Emergent Universal Key for AI features (call auto-tagging, coaching).
                Top up in <span style={{ color: "var(--t2-orange)", fontWeight: 700 }}>Profile → Universal Key → Add Balance</span>
                {" "}from the Emergent workspace when the budget runs low.
              </div>
            </T2Panel>
          </div>
        </div>
      </div>
    </Layout>
  );
}
